import React from 'react';
import { AccessType, CreditLimits } from '../../types';

interface CreditLimitsFormProps {
  limits: CreditLimits;
  onChange: (limits: CreditLimits) => void;
  isOrgAdmin?: boolean;
  disabled?: boolean;
  currentUserRole?: string;
}

const CreditLimitsForm: React.FC<CreditLimitsFormProps> = ({
  limits,
  onChange,
  isOrgAdmin = false,
  disabled = false,
  currentUserRole,
}) => {
  // Convert access_type to array for UI handling
  const accessTypes = Array.isArray(limits.access_type) 
    ? limits.access_type 
    : (limits.access_type || '').split(',').filter((type): type is AccessType => 
        type === 'web' || type === 'ide' || type === 'security' || type === 'devops'
      );

  const handleAccessTypeChange = (type: AccessType) => {
    if (disabled) return;
    
    // Prevent DevOps from being enabled (coming soon) - except for super_admin and org_admin
    if (type === 'devops' && currentUserRole !== 'super_admin' && currentUserRole !== 'org_admin') return;
    
    const newAccessTypes = accessTypes.includes(type)
      ? accessTypes.filter(t => t !== type)
      : [...accessTypes, type];
    
    // Ensure we're passing an array of valid AccessType values
    const validAccessTypes = newAccessTypes.filter((type): type is AccessType => 
      type === 'web' || type === 'ide' || type === 'security' || type === 'devops'
    );
    
    onChange({
      ...limits,
      access_type: validAccessTypes,
    });
  };

  // Calculate approximate token equivalents for display
  const getTokenEquivalent = (credits: number, accessType: AccessType) => {
    if (accessType === 'ide') {
      return `≈ ${credits.toLocaleString()} tokens`;
    } else {
      // For web: 9 credits per million input tokens, 45 credits per million output tokens
      // Show input token equivalent (more generous)
      const inputTokens = (credits / 9) * 1000000;
      return `≈ ${Math.floor(inputTokens).toLocaleString()} input tokens`;
    }
  };

  const primaryAccessType = accessTypes[0] || 'web';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          Credit Limits
        </h3>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Set credit limits and access types for this {isOrgAdmin ? 'user' : 'organization'}.
          Credits provide a unified way to manage usage across different access types.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label htmlFor="credit_limit" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Credit Limit
          </label>
          <div className="mt-2">
            <input
              type="number"
              name="credit_limit"
              id="credit_limit"
              value={limits.credit_limit}
              onChange={(e) => onChange({ ...limits, credit_limit: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              className="block w-full px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Enter credit limit"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-4">
            Access Types
          </label>
          <div className="space-y-4">
            <label className={`flex items-center group ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={accessTypes.includes('ide')}
                  onChange={() => handleAccessTypeChange('ide')}
                  disabled={disabled}
                  className="sr-only"
                />
                <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                  accessTypes.includes('ide')
                    ? 'bg-primary-500'
                    : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                    accessTypes.includes('ide') ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </div>
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors duration-200">
                  IDE Access
                </span>
              </div>
            </label>

            <label className={`flex items-center group ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={accessTypes.includes('web')}
                  onChange={() => handleAccessTypeChange('web')}
                  disabled={disabled}
                  className="sr-only"
                />
                <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                  accessTypes.includes('web')
                    ? 'bg-primary-500'
                    : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                    accessTypes.includes('web') ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </div>
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors duration-200">
                  Web App Access
                </span>
              </div>
            </label>

            <label className={`flex items-center group ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={accessTypes.includes('security')}
                  onChange={() => handleAccessTypeChange('security')}
                  disabled={disabled}
                  className="sr-only"
                />
                <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                  accessTypes.includes('security')
                    ? 'bg-primary-500'
                    : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                    accessTypes.includes('security') ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </div>
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors duration-200">
                  Security Access
                </span>
              </div>
            </label>

            <label className={`flex items-center group ${
              disabled || (currentUserRole !== 'super_admin' && currentUserRole !== 'org_admin') 
                ? 'cursor-not-allowed opacity-50' 
                : 'cursor-pointer'
            }`}>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={accessTypes.includes('devops')}
                  onChange={() => handleAccessTypeChange('devops')}
                  disabled={disabled || (currentUserRole !== 'super_admin' && currentUserRole !== 'org_admin')}
                  className="sr-only"
                />
                <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                  accessTypes.includes('devops')
                    ? 'bg-primary-500'
                    : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                    accessTypes.includes('devops') ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </div>
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors duration-200 flex items-center">
                  DevOps Access
                  {currentUserRole !== 'super_admin' && currentUserRole !== 'org_admin' && (
                    <span className="ml-2 px-2 py-1 text-xs bg-yellow-500/10 text-yellow-500 dark:text-yellow-400 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </span>
              </div>
            </label>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CreditLimitsForm;
